/**
 * Structural game configuration: time system, speeds, rendering.
 * These are gameplay-structural knobs (not economy balance).
 */

/** Available game speeds. PAUSE = 0 means the clock does not advance. */
export const GAME_SPEEDS = [0, 1, 2, 4, 8] as const;
export type GameSpeed = (typeof GAME_SPEEDS)[number];

export const gameConfig = {
  /** Real-world seconds that correspond to one full in-game day at 1x speed. */
  realSecondsPerGameDay: 300, // 1 game day = 5 real minutes (spec section 4)

  /** The simulation advances in fixed steps of this many game-minutes. */
  simulationTickMinutes: 1, // fixed tick = 1 game minute (spec section 4)

  defaultSpeed: 1 as GameSpeed,

  /** Starting date/time for a new game. */
  startDate: {
    year: 1,
    month: 1, // 1-based (Jan)
    day: 1, // 1-based
    hour: 8,
    minute: 0,
  },
} as const;

/**
 * Derived: how many real milliseconds one in-game minute takes at 1x speed.
 * (realSecondsPerGameDay seconds) / (24*60 minutes) * 1000 ms.
 */
export const REAL_MS_PER_GAME_MINUTE =
  (gameConfig.realSecondsPerGameDay * 1000) / (24 * 60);

/**
 * Vehicle simulation tuning (spec sections 9/29). Behavioral knobs kept here
 * (structural), while economy balance stays in economyConfig.ts. Phase 5's
 * DemandEngine will replace the fixed spawn cadence used in Phase 3.
 */
export const simConfig = {
  /** How far a vehicle travels per game-minute, in grid tiles. */
  vehicleSpeedTilesPerMinute: 1.5,
  /** Fixed spawn cadence per eligible lot (until DemandEngine, Phase 5). */
  spawnIntervalMinutes: 4,
  /** Minutes a searching car will wait for a space before abandoning. */
  searchPatienceMinutes: 20,
  /** Time spent at the exit paying (reduced when a cashier exists). */
  payingMinutesBase: 2,
  payingMinutesWithCashier: 1,
  /** Safety cap on concurrent vehicles per lot. */
  maxVehiclesPerLot: 300,
} as const;
